(() => {
  'use strict';
  if (window.__GREMARI_WISHLIST_V1__) return;
  window.__GREMARI_WISHLIST_V1__ = true;

  const STORAGE_KEY = 'gremari_wishlist_v1';
  const EVENT = 'gremari:wishlist:changed';
  const PRODUCT_RE = /\/products\/([^/?#]+)/i;
  const cache = new Map();
  const cfg = () => window.GremariCommerceTools || {};
  const labels = () => cfg().labels || {};
  const routes = () => cfg().routes || {};

  const CARD_SELECTOR = [
    'product-card',
    '.card-product',
    '.product-card',
    '.product-card-wrapper',
    '.card-wrapper',
    '.card--product',
    '.product-grid-item',
    '.grid__item',
    '.gremari-recommendation-card',
    '[data-product-card]'
  ].join(',');

  const MEDIA_SELECTOR = [
    '.card-media',
    '.card__media',
    '.card__inner',
    '.product-card__media',
    '.product-media-container',
    '.media',
    '[data-product-card-media]'
  ].join(',');

  let scanTimer = 0;

  function handleFromUrl(value) {
    try {
      const url = new URL(value, location.origin);
      const match = url.pathname.match(PRODUCT_RE);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      const match = String(value || '').match(PRODUCT_RE);
      return match ? match[1] : '';
    }
  }

  function productUrl(handle, value = '') {
    if (value) {
      try {
        const url = new URL(value, location.origin);
        if (url.pathname.includes('/products/')) return `${url.pathname}${url.search || ''}`;
      } catch (_) {}
    }
    return handle ? `/products/${encodeURIComponent(handle)}` : '#';
  }

  function read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      return parsed.map((item) => ({
        id: String(item.id || ''),
        handle: String(item.handle || handleFromUrl(item.url || '')),
        title: String(item.title || ''),
        url: String(item.url || ''),
        image: String(item.image || ''),
        price: String(item.price || ''),
        variantId: String(item.variantId || ''),
        available: item.available !== false,
        requiresChoice: Boolean(item.requiresChoice)
      })).filter((item) => item.id && !seen.has(item.id) && seen.add(item.id));
    } catch (_) {
      return [];
    }
  }

  function write(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) {}
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
    return items;
  }

  function currency() {
    return window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'EUR';
  }

  function money(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency()
      }).format(value / 100);
    } catch (_) {
      return `${(value / 100).toFixed(2)} ${currency()}`;
    }
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (cache.has(handle)) return cache.get(handle);

    const promise = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })().catch(() => null);

    cache.set(handle, promise);
    return promise;
  }

  function normalizeProduct(product, fallback = {}) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((v) => v?.available) || variants[0] || null;
    const tags = (Array.isArray(product?.tags) ? product.tags : []).map((x) => String(x).toLowerCase());
    const handle = String(product?.handle || fallback.handle || handleFromUrl(fallback.url || ''));
    const requiresChoice =
      variants.length > 1 ||
      tags.includes('non_native_size') ||
      tags.includes('wedding_pair_pricing') ||
      Boolean(fallback.requiresChoice);

    return {
      id: String(product?.id || fallback.id || ''),
      handle,
      title: String(product?.title || fallback.title || ''),
      url: productUrl(handle, product?.url || fallback.url || ''),
      image: String(product?.featured_image || product?.featured_media?.preview_image?.src || fallback.image || ''),
      price: product?.price != null ? money(product.price) : String(fallback.price || ''),
      variantId: String(variant?.id || fallback.variantId || ''),
      available: variant ? Boolean(variant.available) : fallback.available !== false,
      requiresChoice
    };
  }

  function svgHeart() {
    return `<svg class="gm-wishlist-heart-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20.25 4.7 13.2C.55 9.2 6.2 3.15 10.45 7.1L12 8.55l1.55-1.45c4.25-3.95 9.9 2.1 5.75 6.1L12 20.25Z"></path>
    </svg>`;
  }

  function buttonData(button) {
    return {
      id: String(button.dataset.gmWishlistId || ''),
      handle: String(button.dataset.gmWishlistHandle || ''),
      title: String(button.dataset.gmWishlistTitle || ''),
      url: String(button.dataset.gmWishlistUrl || ''),
      image: String(button.dataset.gmWishlistImage || ''),
      price: String(button.dataset.gmWishlistPrice || ''),
      variantId: String(button.dataset.gmWishlistVariantId || ''),
      available: button.dataset.gmWishlistAvailable !== 'false',
      requiresChoice: button.dataset.gmWishlistRequiresChoice === 'true'
    };
  }

  function apply(button, item) {
    button.dataset.gmWishlistId = item.id;
    button.dataset.gmWishlistHandle = item.handle;
    button.dataset.gmWishlistTitle = item.title;
    button.dataset.gmWishlistUrl = item.url;
    button.dataset.gmWishlistImage = item.image;
    button.dataset.gmWishlistPrice = item.price;
    button.dataset.gmWishlistVariantId = item.variantId;
    button.dataset.gmWishlistAvailable = item.available ? 'true' : 'false';
    button.dataset.gmWishlistRequiresChoice = item.requiresChoice ? 'true' : 'false';
    button.disabled = false;
    button.removeAttribute('aria-disabled');
  }

  function setState(button, active) {
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active
      ? (labels().wishlistRemove || 'Remove from wishlist')
      : (labels().wishlistAdd || 'Add to wishlist');
    button.setAttribute('aria-label', label);
    button.title = label;
    const visible = button.querySelector('[data-gm-wishlist-button-label]');
    if (visible) visible.textContent = label;
  }

  function sync(items = read()) {
    const ids = new Set(items.map((x) => x.id));
    document.querySelectorAll('[data-gm-wishlist-toggle]').forEach((button) => {
      setState(button, ids.has(String(button.dataset.gmWishlistId || '')));
    });
    document.querySelectorAll('[data-gm-wishlist-count]').forEach((node) => {
      node.textContent = String(items.length);
      node.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-gm-wishlist-page-count]').forEach((node) => {
      node.textContent = String(items.length);
    });
    renderPages(items);
  }

  async function hydrate(button, handle) {
    const product = await fetchProduct(handle);
    if (!product || !document.documentElement.contains(button)) return;
    apply(button, normalizeProduct(product, buttonData(button)));
    sync();
  }

  function cardProductLink(card) {
    const links = [...(card.querySelectorAll?.('a[href*="/products/"]') || [])];
    return links.find((a) => handleFromUrl(a.href)) || null;
  }

  function decorateCard(card) {
    if (!(card instanceof Element) || card.dataset.gmwReady === '1') return;
    const link = cardProductLink(card);
    const handle = handleFromUrl(link?.href || '');
    if (!handle) return;

    const media = card.querySelector(MEDIA_SELECTOR);
    if (!media) return;

    card.dataset.gmwReady = '1';
    media.classList.add('gm-wishlist-card-media');

    // Disable old Wishlist Hero controls on cards to avoid two independent lists.
    card.querySelectorAll('[class*="wishlist"][class*="hero"],[id*="wishlist-hero"],.wishlist-hero-custom-button').forEach((node) => {
      if (!node.closest('[data-gm-wishlist-toggle]')) node.style.display = 'none';
    });

    if (media.querySelector(':scope > .gm-wishlist-card-button')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gm-wishlist-card-button';
    button.dataset.gmWishlistToggle = '';
    button.dataset.gmWishlistHandle = handle;
    button.dataset.gmWishlistUrl = productUrl(handle, link.href);
    button.dataset.gmWishlistTitle = link.getAttribute('aria-label') || link.textContent.trim() || '';
    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
    button.innerHTML = svgHeart();
    media.appendChild(button);
    hydrate(button, handle);
  }

  function decorateCards(scope = document) {
    if (scope instanceof Element && scope.matches(CARD_SELECTOR)) decorateCard(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(decorateCard);
  }

  function ensureProductButton() {
    if (!/\/products\//.test(location.pathname)) return;
    if (document.querySelector('[data-gm-wishlist-toggle].gm-pdp-wishlist')) return;
    // Our patched PDP has an explicit button. If a legacy/custom PDP does not,
    // the generic auto button is inserted after its product form.
    const info = document.querySelector('.product-info,.product__info-container,.pdp-info,[data-product-information],product-info');
    const form = info?.querySelector('product-form,.product-form,form[action*="/cart/add"]');
    if (!info || !form) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gm-wishlist-auto-product-button';
    button.dataset.gmWishlistToggle = '';
    button.dataset.gmWishlistHandle = handleFromUrl(location.pathname);
    button.setAttribute('aria-disabled', 'true');
    button.disabled = true;
    button.innerHTML = `${svgHeart()}<span data-gm-wishlist-button-label>${labels().wishlistAdd || 'Add to wishlist'}</span>`;
    form.insertAdjacentElement('afterend', button);
    hydrate(button, button.dataset.gmWishlistHandle);
  }

  function buildCard(item, grid) {
    const article = document.createElement('article');
    article.className = 'gm-wishlist-card';

    const media = document.createElement('div');
    media.className = 'gm-wishlist-card__media';

    const link = document.createElement('a');
    link.href = productUrl(item.handle, item.url);
    link.className = 'gm-wishlist-card__image-link';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = item.title || '';
      img.loading = 'lazy';
      img.className = 'gm-wishlist-card__image';
      link.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'gm-wishlist-card__placeholder';
      placeholder.textContent = 'GREMARI';
      link.appendChild(placeholder);
    }
    media.appendChild(link);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'gm-wishlist-card__remove';
    remove.dataset.gmWishlistRemove = item.id;
    remove.innerHTML = svgHeart();
    remove.setAttribute('aria-label', grid.dataset.removeLabel || 'Remove');
    media.appendChild(remove);
    article.appendChild(media);

    const content = document.createElement('div');
    content.className = 'gm-wishlist-card__content';

    const title = document.createElement('h2');
    title.className = 'gm-wishlist-card__title';
    title.innerHTML = `<a href="${productUrl(item.handle, item.url)}"></a>`;
    title.querySelector('a').textContent = item.title || 'Product';
    content.appendChild(title);

    const price = document.createElement('p');
    price.className = 'gm-wishlist-card__price';
    price.textContent = item.price || '';
    content.appendChild(price);

    const actions = document.createElement('div');
    actions.className = 'gm-wishlist-card__actions';

    const view = document.createElement('a');
    view.href = productUrl(item.handle, item.url);
    view.className = 'gm-wishlist-card__link';
    view.textContent = item.requiresChoice
      ? (grid.dataset.chooseOptionsLabel || 'Choose options')
      : (grid.dataset.productLabel || 'View product');
    actions.appendChild(view);

    if (!item.requiresChoice) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'gm-wishlist-card__cart';
      add.dataset.gmWishlistAddCart = '';
      add.dataset.variantId = item.variantId || '';
      add.dataset.handle = item.handle || '';
      add.disabled = item.available === false;
      add.textContent = item.available
        ? (grid.dataset.addCartLabel || 'Add to bag')
        : (grid.dataset.soldOutLabel || 'Unavailable');
      actions.appendChild(add);
    }

    const removeText = document.createElement('button');
    removeText.type = 'button';
    removeText.className = 'gm-wishlist-card__remove-text';
    removeText.dataset.gmWishlistRemove = item.id;
    removeText.textContent = grid.dataset.removeLabel || 'Remove';
    actions.appendChild(removeText);

    content.appendChild(actions);
    article.appendChild(content);
    return article;
  }

  function renderPages(items = read()) {
    document.querySelectorAll('[data-gm-wishlist-page]').forEach((page) => {
      const grid = page.querySelector('[data-gm-wishlist-grid]');
      const empty = page.querySelector('[data-gm-wishlist-empty]');
      if (!grid || !empty) return;
      grid.replaceChildren();

      if (!items.length) {
        grid.hidden = true;
        empty.hidden = false;
        return;
      }

      grid.hidden = false;
      empty.hidden = true;
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(buildCard(item, grid)));
      grid.appendChild(fragment);
    });
  }

  async function refreshPageProducts() {
    if (!document.querySelector('[data-gm-wishlist-page]')) return;
    const items = read();
    if (!items.length) return renderPages([]);

    const fresh = await Promise.all(items.map(async (item) => {
      const product = await fetchProduct(item.handle || handleFromUrl(item.url));
      return product ? normalizeProduct(product, item) : item;
    }));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh)); } catch (_) {}
    sync(fresh);
  }

  function toggle(button) {
    const item = buttonData(button);
    if (!item.id) return;
    const items = read();
    const index = items.findIndex((x) => x.id === item.id);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(item);
    write(items);
  }

  async function addToCart(button) {
    if (button.disabled) return;
    const normal = button.closest('[data-gm-wishlist-grid]')?.dataset.addCartLabel || 'Add to bag';
    const added = button.closest('[data-gm-wishlist-grid]')?.dataset.addedCartLabel || 'Added';
    const error = button.closest('[data-gm-wishlist-grid]')?.dataset.cartErrorLabel || 'Try again';

    button.disabled = true;
    try {
      let variantId = button.dataset.variantId;
      if (!variantId) {
        const p = await fetchProduct(button.dataset.handle || '');
        variantId = normalizeProduct(p || {}, {}).variantId;
      }
      if (!variantId) throw new Error('No variant');

      const route = routes().cartAdd || '/cart/add';
      const response = await fetch(route.endsWith('.js') ? route : `${route}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Cart add failed');

      button.textContent = added;
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      setTimeout(() => { button.textContent = normal; button.disabled = false; }, 1500);
    } catch (_) {
      button.textContent = error;
      setTimeout(() => { button.textContent = normal; button.disabled = false; }, 1500);
    }
  }

  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-gm-wishlist-add-cart]');
    if (add) {
      event.preventDefault(); event.stopPropagation(); addToCart(add); return;
    }
    const remove = event.target.closest('[data-gm-wishlist-remove]');
    if (remove) {
      event.preventDefault(); event.stopPropagation();
      write(read().filter((x) => x.id !== String(remove.dataset.gmWishlistRemove)));
      return;
    }
    const toggleButton = event.target.closest('[data-gm-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault(); event.stopPropagation();
      if (!toggleButton.disabled) toggle(toggleButton);
    }
  }, true);

  document.addEventListener(EVENT, (event) => sync(event.detail?.items || read()));
  document.addEventListener('shopify:section:load', () => scheduleScan());

  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(() => {
      decorateCards(document);
      ensureProductButton();
      sync();
    }, 80);
  }

  function boot() {
    decorateCards(document);
    ensureProductButton();
    sync();
    refreshPageProducts();

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node instanceof Element &&
          (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR))
        )
      );
      if (relevant) scheduleScan();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  window.GremariWishlist = { read, write, fetchProduct, refresh: scheduleScan };
})();
